import { faXmark } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { assetAnimationUrl, assetUrl, itemLabel, itemName } from '../utils/assetUrl'

/**
 * Everything about one item in the inventory.
 *
 * The grid shows what you own; this shows what it is — the full trait list, and
 * whether it exists on-chain at all.
 *
 * That last one matters more than it looks. A skin behaves exactly like a
 * minted weapon in game, so nothing on the card distinguishes them, and
 * somebody could reasonably believe they hold an asset they do not. Hence the
 * note, and the offer to make it real.
 *
 * It portals to the body, and that is not a stylistic choice. The inventory
 * card carries `container-inline` (`container-type: inline-size`), which brings
 * layout containment with it, and a contained element becomes the containing
 * block for `position: fixed` descendants. Rendered in place, this "full
 * screen" overlay was therefore sized to the card and then clipped by the
 * card's own `overflow-hidden` -- so the page never dimmed and the panel came
 * out card-width. The card also sets font-size in `cqi`, which the panel would
 * inherit. Both problems end at the portal.
 */

type ItemDetailProps = {
    nft: NFT & { is_nft?: boolean; traits?: Record<string, string> }
    onClose: () => void
    onConvert?: () => void
}

const Row = ({ label, value }: { label: string; value: string }) => (
    <div className='flex flex-row justify-between gap-3 border-b border-white/5 py-1 text-xs'>
        <span className='text-white/40'>{label}</span>
        <span className='min-w-0 truncate text-right'>{value}</span>
    </div>
)

export const ItemDetail = ({ nft, onClose, onConvert }: ItemDetailProps) => {
    // The animation if the CDN has one, the still if it does not. A missing gif
    // is a 404 rather than an error, so it would otherwise render as a broken
    // image; falling back on `onError` keeps a picture on screen either way.
    const animation = assetAnimationUrl(nft)
    const [source, setSource] = useState(animation ?? assetUrl(nft))
    // Escape closes it: a panel over the page that only a mouse can dismiss is
    // a trap for anybody working from the keyboard.
    useEffect(() => {
        const onKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') onClose()
        }
        document.addEventListener('keydown', onKey)
        // The page behind must not scroll under the panel: a wheel over the
        // overlay would otherwise move the inventory grid out from under it.
        const previous = document.body.style.overflow
        document.body.style.overflow = 'hidden'
        return () => {
            document.removeEventListener('keydown', onKey)
            document.body.style.overflow = previous
        }
    }, [onClose])

    const traits = nft.traits ?? {}
    const isNft = nft.is_nft !== false

    return createPortal(
        <div
            className='fixed inset-0 z-[1000] grid place-items-center bg-black/80 p-4 text-base'
            onClick={onClose}
        >
            <div
                className='max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded border border-white/20 bg-neutral-900 p-4'
                onClick={(e) => e.stopPropagation()}
            >
                <div className='mb-3 flex flex-row items-start justify-between gap-4'>
                    <div className='flex min-w-0 flex-col'>
                        <h2 className='truncate text-lg font-bold'>{itemName(nft)}</h2>
                        <div className='flex flex-row items-center gap-3 text-xs text-white/50'>
                            <span>{itemLabel(nft.operative_type)}</span>
                            {nft.is_legendary && <span className='text-[#FFE500]'>Legendary</span>}
                            <span>{nft.stake_rate} points/day</span>
                            {nft.staked ? <span className='text-green-400'>Earning</span> : null}
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        title='Close'
                        className='rounded border border-white/20 px-2 py-1 text-xs hover:border-white/50'
                    >
                        <FontAwesomeIcon icon={faXmark} />
                    </button>
                </div>

                {!isNft && (
                    <div className='mb-3 flex flex-row items-center justify-between gap-3 rounded border border-amber-400/40 p-2'>
                        <span className='text-xs text-white/60'>
                            This skin works in game and is earning, but it is not yours on chain,
                            so it cannot be sold or traded.
                        </span>
                        <button
                            onClick={onConvert}
                            className='shrink-0 rounded bg-amber-500/90 px-3 py-1 text-xs font-bold uppercase tracking-wide text-black hover:bg-amber-400'
                        >
                            Convert to NFT
                        </button>
                    </div>
                )}

                <div className='flex flex-col gap-4 sm:flex-row'>
                    <div className='sm:w-1/2'>
                        <img
                            src={source}
                            alt={itemName(nft)}
                            onError={() => setSource(assetUrl(nft))}
                            className='h-64 w-full rounded border border-white/10 bg-black/30 object-contain'
                        />
                    </div>
                    <div className='sm:w-1/2'>
                        <h3 className='mb-1 text-xs uppercase tracking-wide text-white/50'>Details</h3>
                        <div className='max-h-64 overflow-y-auto pr-1'>
                            {Object.keys(traits).length === 0 ? (
                                <span className='text-xs text-white/40'>Nothing recorded.</span>
                            ) : (
                                Object.entries(traits).map(([label, value]) => (
                                    <Row key={label} label={label} value={value} />
                                ))
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    )
}

export default ItemDetail
